import type { ReviewDue, ReviewDueInsert, ReviewDueUpdate } from '@/types/customer-ops';

const LAZY_RIVER_LOC_ID = 'loc-040';

const DEV_SEED_REVIEW_DUE: ReviewDue[] = [
  {
    id: 'dev-review-lr-readout',
    location_id: LAZY_RIVER_LOC_ID,
    review_type: 'improvement_campaign_readout',
    due_date: '2026-06-30',
    owner: 'Jen',
    status: 'open',
    meeting_date: null,
    agenda_text: [
      '[customer-ops-pr6-seed]',
      'Improvement campaign readout — Lazy River Dracut',
      '',
      'Agenda:',
      '1. Feb–May campaign baseline vs results',
      '2. Walk proof_lrp_basket_lift, proof_lrp_dracut_sessions_lift, proof_lrp_dracut_spotlight_revenue',
      '3. Tewksbury follow-up (proof_lrp_tewksbury_rfid_engagement)',
      '4. Next floor optimizations',
      '',
      'Proof to reference:',
      '- proof_lrp_basket_lift',
      '- proof_lrp_dracut_sessions_lift',
      '- proof_lrp_dracut_spotlight_revenue',
    ].join('\n'),
    talk_points:
      'Lead with family basket lift brief (/lr-brief-april-2026.html). Confirm Dracut + Tewksbury stakeholders. Capture one follow-up action.',
    proof_metric_ids: [
      'proof_lrp_basket_lift',
      'proof_lrp_dracut_sessions_lift',
      'proof_lrp_dracut_spotlight_revenue',
    ],
    trigger_source: 'manual',
    trigger_key: null,
    created_at: '2026-06-01T10:00:00Z',
    updated_at: '2026-06-01T10:00:00Z',
  },
  {
    id: 'dev-review-lr-tewksbury-readout',
    location_id: 'loc-041',
    review_type: 'improvement_campaign_readout',
    due_date: '2026-07-15',
    owner: 'Jen',
    status: 'open',
    meeting_date: null,
    agenda_text: [
      '[customer-ops-pr6-seed]',
      'Improvement campaign readout — Lazy River Tewksbury',
      '',
      'Agenda:',
      '1. May campaign RFID engagement results',
      '2. Walk proof_lrp_tewksbury_rfid_engagement and family basket lift',
      '3. Coordinate with Dracut readout actions',
      '',
      'Proof to reference:',
      '- proof_lrp_tewksbury_rfid_engagement',
      '- proof_lrp_basket_lift',
    ].join('\n'),
    talk_points:
      'Open Tewksbury May brief (/lazy-river-tewksbury-store-brief-may-2026.html). Align floor optimizations with Dracut plan.',
    proof_metric_ids: ['proof_lrp_tewksbury_rfid_engagement', 'proof_lrp_basket_lift'],
    trigger_source: 'manual',
    trigger_key: null,
    created_at: '2026-06-01T10:00:00Z',
    updated_at: '2026-06-01T10:00:00Z',
  },
];

let devReviewDueRows: ReviewDue[] = [...DEV_SEED_REVIEW_DUE];

function trimNullable(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function meetingDateForStatus(status: ReviewDue['status'], meetingDate: string | null | undefined): string | null {
  const nextMeetingDate = trimNullable(meetingDate);
  if ((status === 'scheduled' || status === 'completed') && !nextMeetingDate) {
    throw new Error('Meeting date is required for scheduled or completed reviews.');
  }
  return nextMeetingDate;
}

function sortRows(rows: ReviewDue[]): ReviewDue[] {
  return [...rows].sort((a, b) => a.due_date.localeCompare(b.due_date));
}

export function mergeDevCustomerOpsReviewDue(rows: ReviewDue[]): ReviewDue[] {
  const byId = new Map<string, ReviewDue>();
  for (const row of [...devReviewDueRows, ...rows]) {
    byId.set(row.id, row);
  }
  return sortRows([...byId.values()]);
}

export function findDevCustomerOpsReviewDueById(id: string): ReviewDue | null {
  return devReviewDueRows.find((row) => row.id === id) ?? null;
}

export function devCustomerOpsReviewDueCreate(data: ReviewDueInsert): ReviewDue {
  if (data.trigger_key) {
    const duplicate = devReviewDueRows.find(
      (row) => row.location_id === data.location_id && row.trigger_key === data.trigger_key
    );
    if (duplicate) {
      throw new Error(`Duplicate trigger key: ${data.trigger_key}`);
    }
  }

  const now = new Date().toISOString();
  const row: ReviewDue = {
    id: `dev-review-${Date.now()}`,
    location_id: data.location_id,
    review_type: data.review_type,
    due_date: data.due_date,
    owner: data.owner?.trim() || null,
    status: data.status ?? 'open',
    meeting_date: data.meeting_date ?? null,
    agenda_text: data.agenda_text ?? '',
    talk_points: data.talk_points ?? '',
    proof_metric_ids: data.proof_metric_ids ?? [],
    trigger_source: data.trigger_source ?? 'manual',
    trigger_key: data.trigger_key?.trim() || null,
    created_at: now,
    updated_at: now,
  };
  devReviewDueRows = [row, ...devReviewDueRows];
  return row;
}

export function devCustomerOpsReviewDueUpdate(id: string, data: ReviewDueUpdate): ReviewDue {
  const index = devReviewDueRows.findIndex((row) => row.id === id);
  if (index === -1) {
    throw new Error('Review due row not found in dev store');
  }
  const existing = devReviewDueRows[index]!;
  const updated: ReviewDue = {
    ...existing,
    review_type: data.review_type ?? existing.review_type,
    due_date: data.due_date ?? existing.due_date,
    owner: data.owner !== undefined ? data.owner?.trim() || null : existing.owner,
    status: data.status ?? existing.status,
    meeting_date: data.meeting_date !== undefined ? data.meeting_date : existing.meeting_date,
    agenda_text: data.agenda_text ?? existing.agenda_text,
    talk_points: data.talk_points ?? existing.talk_points,
    proof_metric_ids: data.proof_metric_ids ?? existing.proof_metric_ids,
    updated_at: new Date().toISOString(),
  };
  devReviewDueRows[index] = updated;
  return updated;
}

async function tryRemoteOrDev<T>(remote: () => Promise<T>, fallback: () => T): Promise<T> {
  try {
    return await remote();
  } catch {
    return fallback();
  }
}

export function createDevReviewDueServiceWrapper(
  service: typeof import('./supabase/review-due').reviewDueService
) {
  return {
    ...service,
    async list(...args: Parameters<typeof service.list>) {
      const remote = await service.list(...args);
      return mergeDevCustomerOpsReviewDue(remote);
    },
    async getById(id: string) {
      const remote = await service.getById(id);
      if (remote) return remote;
      return findDevCustomerOpsReviewDueById(id);
    },
    async create(data: ReviewDueInsert) {
      return tryRemoteOrDev(
        () => service.create(data),
        () => devCustomerOpsReviewDueCreate(data)
      );
    },
    async update(id: string, data: ReviewDueUpdate) {
      if (findDevCustomerOpsReviewDueById(id)) {
        return devCustomerOpsReviewDueUpdate(id, data);
      }
      return tryRemoteOrDev(
        () => service.update(id, data),
        () => devCustomerOpsReviewDueUpdate(id, data)
      );
    },
    async setStatus(id: string, status: ReviewDue['status'], meetingDate?: string | null) {
      const nextMeetingDate = meetingDateForStatus(status, meetingDate);
      if (findDevCustomerOpsReviewDueById(id)) {
        return devCustomerOpsReviewDueUpdate(id, { status, meeting_date: nextMeetingDate });
      }
      return tryRemoteOrDev(
        () => service.setStatus(id, status, nextMeetingDate),
        () => devCustomerOpsReviewDueUpdate(id, { status, meeting_date: nextMeetingDate })
      );
    },
  };
}
